/**
 * Open Cookiebot settings from the shared footer's "Cookie Policy" link.
 *
 * The explicit `#open-cookiebot` / `.open-cookiebot` hooks support links
 * authored directly in Divi or as a WP menu item CSS class. The footer URL check also supports the menu item currently published
 * on the site, so the behavior does not depend on a database-only menu edit.
 */

const COOKIE_POLICY_PATH = "/cookie-policy/";
// The published footer menu item is `<li class="open-cookiebot"><a
// href="#cookie-policy">` — a CLASS on the menu <li> and a hash href, so it
// matched neither the `#open-cookiebot` id nor the /cookie-policy/ path check
// and the click just jumped to a missing `#cookie-policy` anchor. Both forms
// are listed so the link works whichever way it is authored.
const COOKIE_LINK_SELECTOR = [
  "#open-cookiebot",
  ".open-cookiebot a",
  "a.open-cookiebot",
  ".footer-cookie-link",
  ".footer-cookie-link a",
  'a[href="#cookie-policy"]',
].join(", ");
const COOKIEBOT_WIDGET = "#CookiebotWidget";
const COOKIEBOT_WIDGET_BUTTON = "#CookiebotWidget .CookiebotWidget-logo";
const COOKIEBOT_WIDGET_OPEN_CLASS = "CookiebotWidget-open";
const READY_POLL_MS = 100;
const READY_TIMEOUT_MS = 5000;

let pendingOpen = null;

/**
 * @param {HTMLAnchorElement} link
 * @returns {boolean}
 */
const isCookieSettingsLink = (link) => {
  if (link.matches(COOKIE_LINK_SELECTOR)) return true;
  if (!link.closest(".et-l--footer, footer")) return false;

  try {
    return new URL(link.href, window.location.href).pathname === COOKIE_POLICY_PATH;
  } catch {
    return false;
  }
};

const COOKIEBOT_DIALOG = "#CybotCookiebotDialog";
// "Show details" on the consent banner — switches it to the centred modal
// with the Consent / Details / About tabs, opened on Details.
const COOKIEBOT_DETAILS_LINK = "#CybotCookiebotDialogBodyEdgeMoreDetailsLink";
const COOKIEBOT_DETAILS_TAB = "#CybotCookiebotDialogNavDetails";
// On <html> while the dialog is switching from banner to modal.
const COOKIEBOT_OPENING_CLASS = "tfn-cookiebot-opening";

/**
 * Open Cookiebot's cookie MODAL (centred, Details tab), not the bottom banner.
 *
 * `Cookiebot.renew()` re-shows the consent dialog, but in its banner ("Edge")
 * layout across the bottom of the page. Pressing the banner's own "Show
 * details" link is what turns it into the modal, so do the same once the
 * dialog has rendered. The floating widget's button is the fallback when the
 * SDK global is unavailable.
 *
 * @returns {boolean} whether a Cookiebot UI was opened.
 */
const openCookieSettings = () => {
  if (typeof window.Cookiebot?.renew === "function") {
    // Hide the dialog while it is still the banner (see footer.scss), so
    // only the modal is ever seen.
    document.documentElement.classList.add(COOKIEBOT_OPENING_CLASS);
    window.Cookiebot.renew();
    showCookiebotDetailsWhenRendered();
    return true;
  }

  const widget = document.querySelector(COOKIEBOT_WIDGET);
  const widgetButton = document.querySelector(COOKIEBOT_WIDGET_BUTTON);
  if (!widget || !widgetButton) return false;

  if (!widget.classList.contains(COOKIEBOT_WIDGET_OPEN_CLASS)) {
    widgetButton.click();
  }
  return true;
};

/**
 * `renew()` builds the dialog asynchronously. Check every animation frame
 * (not on a 100ms timer) so the switch to the modal happens before the banner
 * is painted, then reveal it. The class is always removed — on success or at
 * the deadline — so the dialog can never stay invisible.
 */
const showCookiebotDetailsWhenRendered = () => {
  const root = document.documentElement;
  const deadline = Date.now() + READY_TIMEOUT_MS;
  const reveal = () => root.classList.remove(COOKIEBOT_OPENING_CLASS);

  const tick = () => {
    const dialog = document.querySelector(COOKIEBOT_DIALOG);
    const target =
      dialog?.querySelector(COOKIEBOT_DETAILS_LINK) ||
      dialog?.querySelector(COOKIEBOT_DETAILS_TAB);
    if (dialog && target && getComputedStyle(dialog).display !== "none") {
      target.click();
      // Reveal once Cookiebot has dropped the banner ("Edge") layout.
      const waitForModal = () => {
        if (!dialog.classList.contains("CybotEdge") || Date.now() >= deadline) {
          reveal();
        } else {
          window.requestAnimationFrame(waitForModal);
        }
      };
      waitForModal();
      return;
    }
    if (Date.now() < deadline) window.requestAnimationFrame(tick);
    else reveal();
  };
  tick();
};

/**
 * Cookiebot is third-party code and can finish after the theme bundle. Preserve
 * an early click briefly instead of swallowing it or starting duplicate polls.
 */
const openCookieSettingsWhenReady = () => {
  if (openCookieSettings() || pendingOpen !== null) return;

  const deadline = Date.now() + READY_TIMEOUT_MS;
  pendingOpen = window.setInterval(() => {
    if (openCookieSettings() || Date.now() >= deadline) {
      window.clearInterval(pendingOpen);
      pendingOpen = null;
    }
  }, READY_POLL_MS);
};

document.addEventListener("click", (event) => {
  const link = event.target?.closest?.("a");
  if (!link || !isCookieSettingsLink(link)) return;

  event.preventDefault();
  openCookieSettingsWhenReady();
});

/**
 * Keep the floating Cookiebot button (and the reCAPTCHA badge) clear of the
 * footer's legal bar.
 *
 * The widget is `position: fixed` at 10px from the bottom, so at the end of
 * every page it sat on top of the "Privacy Policy / (c)2026" bar. While that
 * bar is on screen, publish how far it has risen into the viewport; the CSS in
 * footer.scss lifts the widget to 5px above it (never below its own 10px).
 * The bar's height changes with viewport width as its text wraps, so this is
 * measured live rather than hard-coded.
 */
const FOOTER_LEGAL_BAR =
  ".et-l--footer .et_pb_section_0_tb_footer > .et_pb_row:last-of-type";
const WIDGET_FOOTER_GAP = 5;
let liftFrame = 0;

const updateWidgetFooterLift = () => {
  liftFrame = 0;
  const bar = document.querySelector(FOOTER_LEGAL_BAR);
  if (!bar) return;

  const overlap = window.innerHeight - bar.getBoundingClientRect().top;
  const lift = overlap > 0 ? Math.round(overlap) + WIDGET_FOOTER_GAP : 0;
  document.documentElement.style.setProperty(
    "--footer-legal-bar-lift",
    `${lift}px`,
  );
};

const scheduleWidgetFooterLift = () => {
  if (!liftFrame) liftFrame = window.requestAnimationFrame(updateWidgetFooterLift);
};

window.addEventListener("scroll", scheduleWidgetFooterLift, { passive: true });
window.addEventListener("resize", scheduleWidgetFooterLift);
window.addEventListener("load", scheduleWidgetFooterLift);
scheduleWidgetFooterLift();
